import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';

export interface JiraConnectionSettings {
  configured: boolean;
  siteUrl?: string;
  email?: string;
  tokenMasked?: string;
  configuredBy?: string;
  connectedAt?: string;
  updatedAt?: string;
}

export interface JiraTestResult {
  ok: boolean;
  accountId: string;
  displayName: string;
  emailAddress?: string;
  siteUrl: string;
}

interface UseJiraConnectionResult {
  settings: JiraConnectionSettings | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  test: (input: { siteUrl: string; email: string; apiToken: string }) => Promise<JiraTestResult>;
  save: (input: { siteUrl: string; email: string; apiToken: string }) => Promise<JiraTestResult>;
  disconnect: () => Promise<void>;
}

/**
 * Admin hook for the Jira org-wide connection onboarding.
 * Mirrors the /api/jira/settings endpoints.
 */
export const useJiraConnection = (): UseJiraConnectionResult => {
  const [settings, setSettings] = useState<JiraConnectionSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/jira/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Jira settings');
      setSettings({ configured: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const test = useCallback(async (input: { siteUrl: string; email: string; apiToken: string }) => {
    const response = await axios.post(`${API_URL}/jira/settings/test`, input, {
      withCredentials: true
    });
    return response.data as JiraTestResult;
  }, []);

  const save = useCallback(
    async (input: { siteUrl: string; email: string; apiToken: string }) => {
      const response = await axios.post(`${API_URL}/jira/settings`, input, {
        withCredentials: true
      });
      await refetch();
      return response.data as JiraTestResult;
    },
    [refetch]
  );

  const disconnect = useCallback(async () => {
    await axios.delete(`${API_URL}/jira/settings`, { withCredentials: true });
    await refetch();
  }, [refetch]);

  return { settings, loading, error, refetch, test, save, disconnect };
};

export default useJiraConnection;
